import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import ResizableDataGrid from "components/ResizableDataGrid";
import { useState, useEffect } from "react";
import { useSnackbar } from "notistack";
import axios from "axios";
import MDBadge from "components/MDBadge";
import { format } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import { CircularProgress, Rating, Dialog, DialogTitle, DialogContent, DialogActions, Divider } from "@mui/material";


const DataGrid = ResizableDataGrid;

function AllTestimonials() {
  const auth = localStorage.getItem("authToken");
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [testimonials, setTestimonials] = useState([]);
  const [selectedTestimonial, setSelectedTestimonial] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);


  const columns = [
    {
      field: "testimonial_id",
      headerName: "ID",
      width: 70,
    },
    {
      field: "user_name",
      headerName: "User Name",
      width: 150,
    },
    {
      field: "phone_no",
      headerName: "Phone No",
      width: 150,
    },
    {
      field: "ratings",
      headerName: "Rating",
      width: 150,
      renderCell: (params) => (
        <Rating value={params.row.ratings} readOnly size="small" />
      ),
    },
    {
      field: "comment",
      headerName: "Comment",
      width: 300,
      renderCell: (params) => (
        <MDTypography
          variant="caption"
          fontWeight="medium"
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            width: "100%",
            display: "block",
          }}
        >
          {params.row.comment}
        </MDTypography>
      ),
    },

    {
      field: "status",
      headerName: "Status",
      width: 120,
      renderCell: (params) => {
        let color = "info";
        if (params.row.status === "approved") color = "success";
        if (params.row.status === "rejected") color = "error";
        if (params.row.status === "pending") color = "warning";
        return (
          <MDBadge
            badgeContent={params.row.status}
            container
            color={color}
          />
        );
      },
    },
    {
      field: "created_at",
      headerName: "Date",
      width: 180,
      valueGetter: (params) => {
        const raw = params.row?.created_at;
        if (!raw) return "—";
        try {
          const timeZone = "UTC";
          const zoned = toZonedTime(new Date(raw), timeZone);
          return format(zoned, "dd MMM yyyy, h:mm a");
        } catch {
          return raw;
        }
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 200,
      renderCell: (params) => (
        <MDBox display="flex" gap={1}>
          <MDButton
            variant="gradient"
            color="info"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedTestimonial(params.row);
              setViewModalOpen(true);
            }}
          >
            View
          </MDButton>

          {params.row.status !== "approved" && (
            <MDButton
              variant="gradient"
              color="success"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                updateStatus(params.row.testimonial_id, "approved");
              }}
            >
              Approve
            </MDButton>
          )}
          {params.row.status !== "rejected" && (
            <MDButton
              variant="gradient"
              color="error"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                updateStatus(params.row.testimonial_id, "rejected");
              }}
            >
              Reject
            </MDButton>
          )}

        </MDBox>
      ),

    },
  ];

  useEffect(() => {
    getTestimonials();
  }, []);

  const getTestimonials = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_BASEURL}/admin/testimonial/getall`,
        {
          headers: { Authorization: `Bearer ${auth}` },
        }
      );
      if (res.data.success) {
        setTestimonials(res.data.data);
      } else {
        enqueueSnackbar(res.data.message || "Failed to fetch testimonials", { variant: "error" });
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Error occurred while fetching testimonials.", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      const res = await axios.put(
        `${process.env.REACT_APP_BASEURL}/admin/testimonial/updatestatus`,
        { testimonial_id: id, status: status },
        {
          headers: { Authorization: `Bearer ${auth}` },
        }
      );
      if (res.data.success) {
        enqueueSnackbar(res.data.message, { variant: "success" });
        getTestimonials();
      } else {
        enqueueSnackbar(res.data.message || "Failed to update status", { variant: "error" });
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Error occurred while updating status.", { variant: "error" });
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Testimonials Management
                </MDTypography>
              </MDBox>
              <MDBox pt={3}>
                {loading ? (
                  <MDBox display="flex" justifyContent="center" p={5}>
                    <CircularProgress color="inherit" />
                  </MDBox>
                ) : (
                  <MDBox sx={{ height: 600 }} p={2}>
                    <DataGrid
                      getRowId={(row) => row.testimonial_id}
                      rows={testimonials}
                      columns={columns}
                      onRowClick={(params) => {
                        setSelectedTestimonial(params.row);
                        setViewModalOpen(true);
                      }}
                      initialState={{
                        pagination: {
                          paginationModel: {
                            pageSize: 10,
                          },
                        },
                      }}
                      pageSizeOptions={[10, 25, 50]}
                      disableRowSelectionOnClick
                    />

                  </MDBox>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* View Testimonial Modal */}
      <Dialog
        open={viewModalOpen}
        onClose={() => setViewModalOpen(false)}
        fullWidth
        maxWidth="md"
      >

        <DialogTitle>
          <MDBox display="flex" justifyContent="space-between" alignItems="center">
            <MDTypography variant="h5">Testimonial Details</MDTypography>
          </MDBox>
        </DialogTitle>
        <Divider sx={{ my: 0 }} />
        <DialogContent>
          <MDBox display="flex" flexDirection="column" gap={3}>
            <Grid container spacing={3}>
              {/* Left Column: User Info */}
              <Grid item xs={12} md={6}>
                <MDBox display="flex" flexDirection="column" gap={2}>
                  <MDBox>
                    <MDTypography variant="button" fontWeight="bold" textTransform="capitalize" color="text">
                      User Name
                    </MDTypography>
                    <MDBox mt={0.5}>
                      <MDTypography variant="body2">{selectedTestimonial?.user_name || "N/A"}</MDTypography>
                    </MDBox>
                  </MDBox>

                  <MDBox>
                    <MDTypography variant="button" fontWeight="bold" textTransform="capitalize" color="text">
                      Phone Number
                    </MDTypography>
                    <MDBox mt={0.5}>
                      <MDTypography variant="body2">{selectedTestimonial?.phone_no || "N/A"}</MDTypography>
                    </MDBox>
                  </MDBox>

                  <MDBox>
                    <MDTypography variant="button" fontWeight="bold" textTransform="capitalize" color="text">
                      Submission Date
                    </MDTypography>
                    <MDBox mt={0.5}>
                      <MDTypography variant="body2">
                        {selectedTestimonial?.created_at
                          ? format(toZonedTime(new Date(selectedTestimonial.created_at), "UTC"), "dd MMM yyyy, h:mm a")
                          : "N/A"}
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                </MDBox>
              </Grid>

              {/* Right Column: Rating and Status */}
              <Grid item xs={12} md={6}>
                <MDBox display="flex" flexDirection="column" gap={2}>
                  <MDBox>
                    <MDTypography variant="button" fontWeight="bold" textTransform="capitalize" color="text">
                      Rating
                    </MDTypography>
                    <MDBox mt={0.5} display="flex" alignItems="center">
                      <Rating value={selectedTestimonial?.ratings || 0} readOnly size="medium" />
                      <MDTypography variant="body2" sx={{ ml: 1 }}>
                        ({selectedTestimonial?.ratings || 0}/5)
                      </MDTypography>
                    </MDBox>
                  </MDBox>

                  <MDBox>
                    <MDTypography variant="button" fontWeight="bold" textTransform="capitalize" color="text">
                      Status
                    </MDTypography>
                    <MDBox mt={0.5}>
                      <MDBadge
                        badgeContent={selectedTestimonial?.status || "pending"}
                        color={
                          selectedTestimonial?.status === "approved"
                            ? "success"
                            : selectedTestimonial?.status === "rejected"
                            ? "error"
                            : "warning"
                        }
                        variant="gradient"
                        size="small"
                      />
                    </MDBox>
                  </MDBox>
                </MDBox>
              </Grid>
            </Grid>

            <Divider />

            <MDBox>
              <MDTypography variant="button" fontWeight="bold" textTransform="capitalize" color="text">
                Comment
              </MDTypography>
              <MDBox
                mt={1}
                p={2}
                sx={{
                  backgroundColor: "#f8f9fa",
                  borderRadius: "8px",
                  borderLeft: "4px solid #1A73E8",
                }}
              >
                <MDTypography variant="body2" sx={{ fontStyle: "italic", color: "#495057" }}>
                  "{selectedTestimonial?.comment || "No comment provided."}"
                </MDTypography>
              </MDBox>
            </MDBox>
          </MDBox>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <MDButton
            variant="gradient"
            color="secondary"
            onClick={() => setViewModalOpen(false)}
          >
            Close
          </MDButton>
          {selectedTestimonial?.status !== "approved" && (
            <MDButton
              variant="gradient"
              color="success"
              onClick={() => {
                updateStatus(selectedTestimonial.testimonial_id, "approved");
                setViewModalOpen(false);
              }}
            >
              Approve
            </MDButton>
          )}
          {selectedTestimonial?.status !== "rejected" && (
            <MDButton
              variant="gradient"
              color="error"
              onClick={() => {
                updateStatus(selectedTestimonial.testimonial_id, "rejected");
                setViewModalOpen(false);
              }}
            >
              Reject
            </MDButton>
          )}
        </DialogActions>
      </Dialog>
    </DashboardLayout>

  );
}

export default AllTestimonials;
